import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, Zap, Eye, X, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Minimal: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  Business: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  Kreativ: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  Technologie: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/templates`, { withCredentials: true })
      .then(r => setTemplates(r.data))
      .catch(err => { if (err.response?.status !== 401) console.error(err); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (preview) {
      document.body.style.overflow = "hidden";
      const onKey = e => { if (e.key === "Escape") setPreview(null); };
      window.addEventListener("keydown", onKey);
      return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
    }
  }, [preview]);

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Laden...</div>;

  return (
    <div className="space-y-5 max-w-7xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-0.5">Website-Designs</p>
        <h1 className="font-heading text-2xl font-black tracking-tight">Templates</h1>
        <p className="text-sm text-muted-foreground mt-1">KI-generierte Websites auf Basis dieser Designs. Klicke auf „Live-Vorschau" um das Template in voller Pracht zu sehen.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5">
        {templates.map(t => (
          <div
            key={t.id}
            data-testid={`template-card-${t.id}`}
            className="bg-card border border-border rounded-sm overflow-hidden hover:-translate-y-0.5 hover:border-foreground/20 transition-all duration-200 group"
          >
            <button
              type="button"
              onClick={() => setPreview(t)}
              data-testid={`template-preview-btn-${t.id}`}
              className="block w-full text-left aspect-video bg-muted overflow-hidden relative cursor-pointer"
            >
              <img
                src={t.thumbnail}
                alt={t.name}
                className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                onError={e => e.target.style.display = "none"}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute top-3 left-3">
                <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${CATEGORY_COLORS[t.category] || "bg-muted text-muted-foreground"}`}>
                  {t.category}
                </span>
              </div>
              <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded-sm">
                <Zap size={10} /> KI-generiert
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="inline-flex items-center gap-2 bg-white text-black px-4 py-2 text-sm font-semibold rounded-sm shadow-lg">
                  <Eye size={14} /> Live-Vorschau
                </span>
              </div>
            </button>

            <div className="p-5">
              <h3 className="font-heading font-bold text-base text-foreground mb-1">{t.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">{t.description}</p>

              <div className="flex items-center gap-2 mb-3">
                <div className="w-4 h-4 rounded-full border border-border" style={{ backgroundColor: t.primary_color }} />
                <span className="text-xs font-mono text-muted-foreground">{t.primary_color}</span>
              </div>

              <div className="flex flex-wrap gap-1.5">
                {t.features.map(f => (
                  <span key={f} className="flex items-center gap-1 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-sm">
                    <CheckCircle size={10} className="text-green-500" /> {f}
                  </span>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between gap-2">
                <p className="text-xs text-muted-foreground">Wählbar bei Projekterstellung.</p>
                <button
                  type="button"
                  onClick={() => setPreview(t)}
                  data-testid={`template-open-preview-${t.id}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-foreground hover:text-primary transition-colors"
                >
                  <Eye size={12} /> Live-Vorschau
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <div
          data-testid="template-preview-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex flex-col"
          onClick={() => setPreview(null)}
        >
          <div
            className="flex items-center justify-between px-6 py-3 bg-zinc-950 text-white border-b border-zinc-800"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-semibold">{preview.name}</span>
              <span className="text-xs text-zinc-400">Live-Vorschau · Beispielfirma „Nova Studios"</span>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={`${API}/templates/${preview.id}/preview`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="template-preview-open-new-tab"
                className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white transition-colors"
              >
                <ExternalLink size={12} /> Neuer Tab
              </a>
              <button
                type="button"
                onClick={() => setPreview(null)}
                data-testid="template-preview-close"
                className="inline-flex items-center gap-1 text-xs bg-zinc-800 hover:bg-zinc-700 text-white px-3 py-1.5 rounded-sm transition-colors"
              >
                <X size={14} /> Schließen
              </button>
            </div>
          </div>
          <div className="flex-1 bg-zinc-950 p-4" onClick={e => e.stopPropagation()}>
            <iframe
              key={preview.id}
              src={`${API}/templates/${preview.id}/preview`}
              title={`Vorschau ${preview.name}`}
              className="w-full h-full bg-white rounded-sm shadow-2xl"
              data-testid="template-preview-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
